import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  CircularProgress,
  Paper,
  Avatar,
  Tabs,
  Tab,
  CssBaseline,
} from '@mui/material';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import LocationCityIcon from '@mui/icons-material/LocationCity';
import { useUser } from '../../contexts/UserContext';
import BadgeIcon from '@mui/icons-material/Badge';
import HofDetailsForm from './HofDetailsForm';
import SectorDetailsForm from './SectorDetailsForm';
import { yellow } from '../../styles/ThemePrimitives';
import { brown } from '../../styles/ThemePrimitives';
import PersonIcon from '@mui/icons-material/Person';
import OverviewTable from './OverviewTable';
import FamilyDetails from './FamilyDetails';
import ThaliDetails from './ThaliDetails';
import AppTheme from '../../styles/AppTheme';
import divider from '../../assets/divider.png';


function MumeneenDetailsTable() {
  const { family_id } = useParams();
  const { token, loading } = useUser();
  const [details, setDetails] = useState(null);
  const [error, setError] = useState(null);
  const [selectedTab, setSelectedTab] = useState(0);

  useEffect(() => {
    if (loading || !token) return;

    const fetchDetails = async () => {
      try {
        const userResponse = await fetch(`https://api.fmb52.com/api/mumeneen/user/${family_id}`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        });

        if (!userResponse.ok) {
          throw new Error(`Error ${userResponse.status}: ${userResponse.statusText}`);
        }

        const userData = await userResponse.json();
        if (userData.data) {
          setDetails(userData.data);
        } else {
          throw new Error('No details found.');
        }
      } catch (error) {
        console.error('Error fetching user details:', error);
        setError('The data is currently unavailable, but we are working to resolve this. Thank you for your patience!');
      }
    };

    fetchDetails();
  }, [family_id, token, loading]);

  if (loading || !details) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        {error ? (
          <Typography variant="h6" color="error">
            {error}
          </Typography>
        ) : (
          <CircularProgress />
        )}
      </Box>
    );
  }

  const handleTabChange = (event, newValue) => {
    setSelectedTab(newValue);
  };

  const getAvatarName = () => {
    return details.name ? details.name.charAt(0).toUpperCase() : '';
  };

  return (
    <AppTheme>
      <CssBaseline />
      <Box
      sx={{
        display: 'flex',
        flexDirection: { xs: 'column', sm: 'row' },
        flexWrap: 'wrap',
        gap: 2,
        mt: 12, pr: 2, pb: 3, pl: 2,
      }}
    >
      <Paper
        sx={{
          padding: 3,
          backgroundColor: '#ffffff',
          borderRadius: '8px',
          boxShadow: 3,
          flex: '0 1 300px',
          width: { xs: '100%', sm: '48%', md: '48%' },
          height: '100%'
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: 'center',
            gap: 2,
            mb: 3,
            borderBottom: '2px solid #8B4513',
            paddingBottom: 2,
          }}
        >
          <Avatar
            alt={details.name}
            src={details.photo?.file_url || '/static/images/avatar-placeholder.png'}
            sx={{
              width: { xs: 120, sm: 120, md: 150 },
              height: { xs: 120, sm: 120, md: 180 },
              border: '2px solid #ddd',
              borderRadius: '8px',
            }}
          >
            {details.photo ? null : getAvatarName()}
          </Avatar>
          <Box>
            <Typography variant="h6" sx={{ paddingBottom: 1, color: brown[700] }}>{details.name}</Typography>

            {details.its && (
              <Typography
                variant="body1"
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'flex-start', paddingBottom: 1 }}>
                  <BadgeIcon
                    sx={{
                      fontSize: 22,
                      verticalAlign: 'middle',
                      color: yellow[200],
                      marginRight: 1,
                    }}
                  />
                  <Typography variant="body1" sx={{ fontSize: 16, fontWeight: 600, color: yellow[400] }}>
                    ITS:
                  </Typography>
                </Box>
                <Box sx={{ color: brown[400] }}>{details.its || 'N/A'}</Box>
              </Typography>
            )}

            {details.mobile && (
              <Typography
                variant="body1"
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                  <PhoneIcon
                    sx={{
                      fontSize: 22,
                      verticalAlign: 'middle',
                      color: yellow[200],
                      marginRight: 1,
                    }}
                  />
                  <Typography variant="body1" sx={{ fontSize: 16, fontWeight: 600, color: yellow[400] }}>
                    Mobile:
                  </Typography>
                </Box>
                <Box sx={{ color: brown[400], marginLeft: 2, paddingTop: 0.3}}>
                  <a
                  href={`https://wa.me/+91${details.mobile.replace(/[^0-9]/g, '')}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    color: brown[400],
                    textDecoration: 'none',
                  }}
                  onMouseEnter={(e) => (e.target.style.color = brown[600])}
                  onMouseLeave={(e) => (e.target.style.color = brown[400])}
                >
                  {details.mobile}
                </a>
                </Box>
              </Typography>
            )}
          </Box>
        </Box>

        <Box
          sx={{
            paddingBottom: 2,
            marginBottom: 2,
          }}
        >
          {details.email && (<Typography variant="body1" sx={{ padding: 1, display: 'flex', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <EmailIcon
                sx={{
                  fontSize: 22,
                  verticalAlign: 'middle',
                  color: yellow[200],
                  marginRight: 1,
                }}
              />
              <Typography variant="body1" sx={{ fontSize: 16, fontWeight: 600, color: yellow[400] }}>Email:</Typography>
            </Box>
            <Box sx={{ color: brown[400] }}>{details.email || 'N/A'}</Box>
          </Typography>)}

          <Typography variant="body1" sx={{ padding: 1, display: 'flex', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <LocationCityIcon
                sx={{
                  fontSize: 22,
                  verticalAlign: 'middle',
                  color: yellow[200],
                  marginRight: 1,
                }}
              />
              <Typography variant="body1" sx={{ fontSize: 16, fontWeight: 600, color: yellow[400] }}>Sector:</Typography>
            </Box>
            <Box sx={{ color: brown[400] }}>{details.sector_name || 'N/A'}-{details.sub_sector_name}</Box>
          </Typography>

          <Typography variant="body1" sx={{ padding: 1, display: 'flex', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <PersonIcon
                sx={{
                  fontSize: 22,
                  verticalAlign: 'middle',
                  color: yellow[200],
                  marginRight: 1,
                }}
              />
              <Typography variant="body1" sx={{ fontSize: 16, fontWeight: 600, color: yellow[400] }}>Masool:</Typography>
            </Box>
            <Box sx={{ color: brown[400] }}>{details.incharge_name || 'N/A'}</Box>
          </Typography>

          <Typography variant="body1" sx={{ padding: 1, display: 'flex', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <PhoneIcon
                sx={{
                  fontSize: 22,
                  verticalAlign: 'middle',
                  color: yellow[200],
                  marginRight: 1,
                }}
              />
              <Typography variant="body1" sx={{ fontSize: 16, fontWeight: 600, color: yellow[400] }}>Masool Mobile No:</Typography>
            </Box>
            <Box sx={{ color: brown[400] }}>{details.incharge_mobile || 'N/A'}</Box>
          </Typography>
        </Box>
      </Paper>

      <Paper
        sx={{
          padding: 2,
          borderRadius: '8px',
          boxShadow: 3,
          backgroundColor: '#ffffff',
          flex: 1,
          width: { xs: '100%', sm: '48%', md: '48%' },
          height: '100%'
        }}
      >
        <Tabs
          value={selectedTab}
          onChange={handleTabChange}
          aria-label="Menu Tabs"
          textColor="primary"
          indicatorColor="primary"
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            '.MuiTabs-indicator': {
              backgroundColor: '#795548',
              height: '3px',
            },
            '.MuiTab-root': {
              fontWeight: 500,
              fontSize: '14px',
              color: '#555',
              textTransform: 'none',
            },
            '.Mui-selected': {
              color: '#333',
              fontWeight: 'bold',
            },
          }}
        >
          <Tab label="Overview" />
          <Tab label="HOF Details" />
          <Tab label="Family Details" />
          <Tab label="Sector Details" />
          <Tab label="Thali Details" />
        </Tabs>
        <Box
          sx={{
            mt: 2,
            position: 'relative',
            marginLeft: '-16px',
            marginRight: '-16px',
            height: {
              xs: 10,
              sm: 15,
              md: 15,
              lg: 15,
              xl: 15,
            },
            backgroundImage: `url(${divider})`,
            backgroundSize: 'contain',
            backgroundRepeat: 'repeat-x',
            backgroundPosition: 'center',
          }}
        />

        <Box>
          {selectedTab === 0 && <OverviewTable familyId={family_id} /> }
          {selectedTab === 1 && <HofDetailsForm familyId={family_id} id={details.id} />}
          {selectedTab === 2 && <FamilyDetails familyId={details.family_id} /> }
          {selectedTab === 3 && <SectorDetailsForm familyId={family_id} />}
          {selectedTab === 4 && <ThaliDetails />}
        </Box>
      </Paper>
    </Box>
    </AppTheme>
  );
}

export default MumeneenDetailsTable;
